import React, { useState, useRef, useEffect } from 'react';
import { workplaceDocumentApi } from '../../services/api';

interface WorkplaceLookupItem {
  id: number;
  title: string;
  sgkNo: string;
  dangerClass: string;
}

interface ArchiveUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  workplaces: WorkplaceLookupItem[];
  selectedWorkplaceId?: string;
  docTypeLabels: Record<string, string>;
}

const ArchiveUploadModal: React.FC<ArchiveUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  workplaces,
  selectedWorkplaceId,
  docTypeLabels
}) => {
  const [targetWorkplaceId, setTargetWorkplaceId] = useState<string>('');
  const [documentType, setDocumentType] = useState<string>('riskAnalysis');
  const [documentDate, setDocumentDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [wpSearch, setWpSearch] = useState<string>('');
  const [isWpDropdownOpen, setIsWpDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (selectedWorkplaceId && selectedWorkplaceId !== 'all') {
        setTargetWorkplaceId(selectedWorkplaceId);
      } else if (workplaces.length > 0) {
        setTargetWorkplaceId(String(workplaces[0].id));
      }
      setSelectedFile(null);
      setNotes('');
      setErrorMsg('');
      setDocumentDate(new Date().toISOString().split('T')[0]);
    }
  }, [isOpen, selectedWorkplaceId, workplaces]);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsWpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!isOpen) return null;

  const currentWp = workplaces.find(w => String(w.id) === targetWorkplaceId);

  const filteredWorkplaces = workplaces.filter(w =>
    (w.title || '').toLocaleLowerCase('tr-TR').includes(wpSearch.toLocaleLowerCase('tr-TR')) ||
    (w.sgkNo || '').includes(wpSearch)
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setErrorMsg('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setErrorMsg('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetWorkplaceId) {
      setErrorMsg('Lütfen belgenin ait olduğu işyerini seçin.');
      return;
    }
    if (!documentType) {
      setErrorMsg('Lütfen belge türünü seçin.');
      return;
    }
    if (!selectedFile) {
      setErrorMsg('Lütfen yüklenecek bir dosya seçin.');
      return;
    }

    setUploading(true);
    setErrorMsg('');

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('documentType', documentType);
      formData.append('documentDate', documentDate);
      if (notes.trim()) {
        formData.append('notes', notes.trim());
      }

      await workplaceDocumentApi.uploadDocument(parseInt(targetWorkplaceId, 10), formData);
      setUploading(false);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Belge yükleme hatası:', err);
      setErrorMsg(err?.response?.data?.message || 'Dosya yüklenirken bir hata meydana geldi.');
      setUploading(false);
    }
  };

  return (
    <div className="archive-upload-modal-overlay" onClick={onClose}>
      <div className="archive-upload-modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal-header">
          <div className="upload-header-title">
            <span className="upload-title-icon">📤</span>
            <h3>Arşive Yeni Resmi Belge Yükle</h3>
          </div>
          <button type="button" className="upload-modal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="upload-modal-form">
          {errorMsg && (
            <div className="upload-modal-error">
              <span>⚠️</span> {errorMsg}
            </div>
          )}

          {/* 1. İşyeri Seçimi */}
          <div className="upload-form-group">
            <label className="upload-form-label">
              İşyeri / Firma <span className="req">*</span>
            </label>
            <div className="custom-wp-select-wrapper" ref={dropdownRef}>
              <div
                className="custom-wp-select-trigger"
                onClick={() => setIsWpDropdownOpen(!isWpDropdownOpen)}
              >
                <span>{currentWp ? `🏢 ${currentWp.title}` : 'İşyeri Seçiniz...'}</span>
                <span className="select-arrow">▼</span>
              </div>

              {isWpDropdownOpen && (
                <div className="custom-wp-dropdown-menu">
                  <div className="wp-dropdown-search">
                    <input
                      type="text"
                      placeholder="Firma ara..."
                      value={wpSearch}
                      onChange={(e) => setWpSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <div className="wp-dropdown-list">
                    {filteredWorkplaces.map(w => (
                      <div
                        key={w.id}
                        className={`wp-dropdown-item ${String(w.id) === targetWorkplaceId ? 'active' : ''}`}
                        onClick={() => {
                          setTargetWorkplaceId(String(w.id));
                          setIsWpDropdownOpen(false);
                          setWpSearch('');
                        }}
                      >
                        <strong>{w.title}</strong>
                        <small>SGK: {w.sgkNo || '-'}</small>
                      </div>
                    ))}
                    {filteredWorkplaces.length === 0 && (
                      <div className="wp-dropdown-empty">Firma bulunamadı</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 2. Belge Türü & Belge Tarihi */}
          <div className="upload-form-row">
            <div className="upload-form-group flex-1">
              <label className="upload-form-label">
                Resmi Evrak Türü <span className="req">*</span>
              </label>
              <select
                className="upload-form-select"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
              >
                {Object.entries(docTypeLabels).map(([code, title]) => (
                  <option key={code} value={code}>
                    {title}
                  </option>
                ))}
              </select>
            </div>

            <div className="upload-form-group" style={{ width: '180px' }}>
              <label className="upload-form-label">
                Belge Düzenlenme Tarihi <span className="req">*</span>
              </label>
              <input
                type="date"
                className="upload-form-input"
                value={documentDate}
                onChange={(e) => setDocumentDate(e.target.value)}
                required
              />
            </div>
          </div>

          {/* 3. Dosya Seçici (Sürükle-Bırak Alanı) */}
          <div className="upload-form-group">
            <label className="upload-form-label">
              Dosya (PDF, Word, Excel, Resim - Max 100MB) <span className="req">*</span>
            </label>
            <div
              className={`upload-dropzone ${selectedFile ? 'has-file' : ''}`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.bmp,.tiff,.heic,.heif,image/*"
              />

              {selectedFile ? (
                <div className="dropzone-file-selected">
                  <span className="dropzone-file-icon">📄</span>
                  <div className="dropzone-file-details">
                    <strong>{selectedFile.name}</strong>
                    <span>{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</span>
                  </div>
                  <button
                    type="button"
                    className="dropzone-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="dropzone-placeholder">
                  <span className="dropzone-cloud-icon">☁️</span>
                  <p>
                    Dosyayı buraya sürükleyin veya <strong>Gözatmak için tıklayın</strong>
                  </p>
                  <small>Desteklenen: PDF, DOCX, XLSX, JPG, PNG</small>
                </div>
              )}
            </div>
          </div>

          {/* 4. Notlar */}
          <div className="upload-form-group">
            <label className="upload-form-label">Açıklama / Notlar (Opsiyonel)</label>
            <input
              type="text"
              className="upload-form-input"
              placeholder="Örn: 2026 yılı revizyonu, uzman onaylı nüsha..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* Alt Butonlar */}
          <div className="upload-modal-footer">
            <button
              type="button"
              className="upload-cancel-btn"
              onClick={onClose}
              disabled={uploading}
            >
              Vazgeç
            </button>
            <button
              type="submit"
              className="upload-submit-btn"
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <span className="btn-spinner"></span>
                  Yükleniyor...
                </>
              ) : (
                <>📤 Arşive Kaydet</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArchiveUploadModal;
